import { useEffect, useRef, useState } from "react";
import type { AUGCRecord } from "@/mocks/augcHistory";
import { ApiError, apiGet, apiSend } from "@/lib/api";
import {
  GENERATION_MODELS,
  executionOptionsFor,
  isQwenModel,
  type ExecutionOptions,
  type GenerationConfig,
  type GenerationModel,
  type PendingRegeneration,
  type RegenerationResponse,
} from "@/services/generationOptions";

interface Props {
  record: AUGCRecord;
  pending?: PendingRegeneration;
  onPendingChange: (id: string, pending: PendingRegeneration | null, expectedKey?: string) => boolean;
  onAccepted: (id: string, key: string) => void;
  onClose: () => void;
}

export default function RegenerateRecordDialog({ record, pending, onPendingChange, onAccepted, onClose }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inFlight = useRef(false);
  const [config, setConfig] = useState<GenerationConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [configVersion, setConfigVersion] = useState(0);
  const [options, setOptions] = useState<ExecutionOptions | null>(pending?.body.executionOptions ?? record.executionOptions ?? null);
  const [defaultUseCutout, setDefaultUseCutout] = useState<boolean | null>(null);
  const [useCutout, setUseCutout] = useState<boolean | undefined>(pending ? pending.body.useCutout : record.useCutout);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setConfigError("");
    apiGet<{ generation: GenerationConfig; useCutout?: boolean }>("/faceswap/wan-config", controller.signal)
      .then(({ generation, useCutout: configuredUseCutout }) => {
        if (!generation?.default || !GENERATION_MODELS.includes(generation.default.model) || !generation.models?.length) {
          throw new Error("服务端未提供有效的生成模型配置");
        }
        setConfig(generation);
        setOptions((current) => current ?? executionOptionsFor(generation.default.model, generation.default.promptExtend ?? true));
        const currentDefault = configuredUseCutout ?? true;
        setDefaultUseCutout(currentDefault);
        setUseCutout((current) => current ?? record.useCutout ?? currentDefault);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setConfigError(err instanceof Error ? err.message : String(err));
      });
    return () => controller.abort();
  }, [configVersion]);

  const selected = pending?.body.executionOptions ?? options;
  const selectedUseCutout = pending ? pending.body.useCutout : useCutout;
  const frozen = Boolean(pending) || busy;

  const submit = async () => {
    if (inFlight.current || (!pending && (!selected || !config || typeof selectedUseCutout !== "boolean"))) return;
    const tuple: PendingRegeneration = pending ?? {
      key: crypto.randomUUID(),
      body: {
        expectedAttemptId: record.currentAttemptId ?? record.id,
        executionOptions: executionOptionsFor(selected!.model, selected!.promptExtend ?? true),
        useCutout: selectedUseCutout!,
      },
    };
    inFlight.current = true;
    setBusy(true);
    setError("");
    let persisted = Boolean(pending);
    try {
      // Persist before sending; after any uncertain response replay this exact tuple.
      if (!pending) onPendingChange(record.id, tuple);
      persisted = true;
      const response = await apiSend<RegenerationResponse>("POST", `/faceswap/records/${record.id}/regenerate`, tuple.body, {
        headers: { "Idempotency-Key": tuple.key },
      });
      if (!response.jobId || (response.retryIdempotencyKey && response.retryIdempotencyKey !== tuple.key)) {
        throw new Error("服务端响应未确认本次重生成请求，请重试确认");
      }
      onAccepted(record.id, tuple.key);
    } catch (err) {
      if (!persisted) {
        setError(`无法保存待确认请求，未发送生成任务：${err instanceof Error ? err.message : String(err)}`);
      } else if (err instanceof ApiError && err.status < 500 && err.status !== 408 && err.status !== 401 && err.status !== 403) {
        onPendingChange(record.id, null, tuple.key);
        setError(`无法重新生成：${err.message}。当前结果未变。`);
      } else {
        setError(`提交结果尚未确认：${err instanceof Error ? err.message : String(err)}。选项已冻结，请使用相同请求重试确认，避免重复生成。`);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="regenerate-record-title"
      aria-describedby="regenerate-record-description"
      onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
      className="m-auto w-[calc(100%_-_2rem)] max-w-lg rounded-xl border border-gray-200 bg-white p-0 shadow-xl backdrop:bg-black/40"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h2 id="regenerate-record-title" className="text-lg font-semibold text-gray-900">重新生成最终照片</h2>
        <button type="button" aria-label="关闭重生成对话框" disabled={busy} onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40"><i className="ri-close-line" /></button>
      </div>
      <div className="space-y-4 p-5">
        <p id="regenerate-record-description" className="text-sm text-gray-600">{record.templateName}：先验证原自拍、模板与资源，验证通过后入队。原结果在新结果发布前保持不变；本次模型与参考图选择均不修改全局默认，也不影响 Photo 业务。</p>
        <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
          <div>当前尝试：<span className="break-all font-mono">{record.currentAttemptId ?? record.id}</span></div>
          <div>上次执行模型：<span className="font-mono">{record.executionOptions?.model ?? "历史记录未保存模型"}</span></div>
          {record.executionOptions && isQwenModel(record.executionOptions.model) && <div>上次 Qwen 提示词改写：{record.executionOptions.promptExtend === false ? "关闭" : "开启"}</div>}
          <div>上次抠像参考图策略：{typeof record.useCutout === "boolean" ? record.useCutout ? "开启" : "关闭" : "未记录"}</div>
        </div>
        {configError && <div role="alert" className="text-sm text-red-600">读取默认配置失败：{configError} <button type="button" disabled={busy} onClick={() => setConfigVersion((value) => value + 1)} className="underline">重新读取</button></div>}
        {!config && !configError && <p role="status" className="text-sm text-gray-500">正在读取生成模型配置…</p>}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="regeneration-model" className="text-sm font-medium text-gray-700">本次生成模型</label>
            <button type="button" disabled={frozen || !config} onClick={() => { if (config) setOptions(executionOptionsFor(config.default.model, config.default.promptExtend ?? true)); }} className="text-xs text-teal-600 hover:underline disabled:opacity-40">使用当前默认模型</button>
          </div>
          <select id="regeneration-model" value={selected?.model ?? ""} disabled={frozen || !config} onChange={(event) => setOptions(executionOptionsFor(event.target.value as GenerationModel))} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:opacity-60">
            {!selected && <option value="" disabled>等待服务端配置</option>}
            {GENERATION_MODELS.map((model) => <option key={model} value={model} disabled={Boolean(config) && !config!.models.some((capability) => capability.name === model)}>{model}</option>)}
          </select>
        </div>
        {selected && isQwenModel(selected.model) && <label className="flex items-center gap-3 text-sm text-gray-700"><input type="checkbox" role="switch" checked={selected.promptExtend ?? true} disabled={frozen} onChange={(event) => setOptions(executionOptionsFor(selected.model, event.target.checked))} className="h-4 w-4 accent-teal-600" />Qwen 提示词改写</label>}
        <div className="space-y-2 rounded-lg border border-gray-200 p-3">
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="regeneration-use-cutout" className="text-sm font-medium text-gray-700">使用抠像后的图作为参考图</label>
            <button type="button" disabled={frozen || defaultUseCutout === null} onClick={() => { if (defaultUseCutout !== null) setUseCutout(defaultUseCutout); }} className="shrink-0 text-xs text-teal-600 hover:underline disabled:opacity-40">使用当前默认参考图策略</button>
          </div>
          {typeof selectedUseCutout === "boolean" ? (
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input id="regeneration-use-cutout" type="checkbox" role="switch" checked={selectedUseCutout} disabled={frozen} onChange={(event) => setUseCutout(event.target.checked)} className="h-4 w-4 accent-teal-600" />
              {selectedUseCutout ? "开启：先尝试抠像，失败时沿用原图" : "关闭：跳过抠像，直接使用保留的原自拍"}
            </label>
          ) : <p className="text-sm text-gray-500">{pending ? "未记录：历史待确认请求保持原始请求，不补入当前默认策略。" : "正在读取默认参考图策略…"}</p>}
          <p className="text-xs text-gray-500">参考图策略与模型、提示词改写独立；关闭时不会沿用已有的抠像图。</p>
        </div>
        {pending && <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">存在待确认请求，模型、改写和参考图策略已冻结。重试仅确认同一请求，不新建另一条任务。</p>}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </div>
      <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
        <button type="button" disabled={busy} onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 disabled:opacity-40">{pending ? "稍后确认" : "取消"}</button>
        <button type="button" disabled={busy || (!pending && (!selected || !config || typeof selectedUseCutout !== "boolean"))} onClick={() => void submit()} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-40">{busy ? "正在确认…" : pending ? "重试确认同一请求" : "确认重新生成"}</button>
      </div>
    </dialog>
  );
}
